import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CASE_TYPES, STATUS_COLOR, fmtDate } from "@/lib/utils-domain";
import { Plus, MagnifyingGlass, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Cases() {
    const [cases, setCases] = useState([]);
    const [clients, setClients] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("All");
    const [open, setOpen] = useState(false);

    const load = async () => {
        const [c, cl] = await Promise.all([api.get("/cases"), api.get("/clients")]);
        setCases(c.data);
        setClients(cl.data);
    };
    useEffect(() => { load(); }, []);

    const visible = cases.filter((c) => {
        const matchesQ = !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.client_name.toLowerCase().includes(q.toLowerCase());
        const matchesS = filter === "All" || c.status === filter;
        return matchesQ && matchesS;
    });

    return (
        <div className="max-w-7xl mx-auto animate-fade-up">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500">Matter management</div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight">All cases</h1>
                    <p className="text-sm text-slate-500 mt-2">{visible.length} of {cases.length} cases</p>
                </div>
                <button onClick={() => setOpen(true)} data-testid="new-case-button" className="btn-primary inline-flex items-center gap-2">
                    <Plus size={14} weight="bold" /> New case
                </button>
            </div>

            {/* Filter bar */}
            <div className="card-sharp p-3 flex flex-col sm:flex-row gap-3 mb-6">
                <div className="flex items-center gap-2 flex-1 px-3 py-1.5 border border-slate-200 rounded-sm bg-white">
                    <MagnifyingGlass size={14} className="text-slate-400" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by case or client…"
                        className="bg-transparent text-sm flex-1 focus:outline-none"
                        data-testid="cases-search-input"
                    />
                </div>
                <div className="flex gap-1 flex-wrap">
                    {["All", "Intake", "Discovery", "Negotiation", "Trial", "Closed"].map((s) => (
                        <button
                            key={s}
                            onClick={() => setFilter(s)}
                            data-testid={`filter-${s.toLowerCase()}`}
                            className={`px-3 py-1.5 rounded-sm text-xs font-semibold border transition ${
                                filter === s ? "bg-[#0B2447] text-white border-[#0B2447]" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                            }`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>

            {/* Table */}
            <div className="card-sharp overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr className="text-left text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
                            <th className="px-5 py-3 font-semibold">Case</th>
                            <th className="px-5 py-3 font-semibold">Client</th>
                            <th className="px-5 py-3 font-semibold">Type</th>
                            <th className="px-5 py-3 font-semibold">Status</th>
                            <th className="px-5 py-3 font-semibold">Opened</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {visible.map((c) => (
                            <tr key={c.case_id} className="hover:bg-slate-50/60 transition group">
                                <td className="px-5 py-4">
                                    <Link to={`/app/cases/${c.case_id}`} className="font-semibold text-slate-900 group-hover:text-[#0B2447]" data-testid={`case-link-${c.case_id}`}>
                                        {c.name}
                                    </Link>
                                </td>
                                <td className="px-5 py-4 text-slate-700">{c.client_name}</td>
                                <td className="px-5 py-4 text-slate-500">{c.case_type}</td>
                                <td className="px-5 py-4"><span className={`badge ${STATUS_COLOR[c.status]}`}>{c.status}</span></td>
                                <td className="px-5 py-4 text-slate-500 font-mono text-xs">{fmtDate(c.opened_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {visible.length === 0 && (
                    <div className="text-center py-16 text-sm text-slate-500">
                        No cases match your filters.
                    </div>
                )}
            </div>

            {open && <NewCaseModal onClose={() => setOpen(false)} clients={clients} onCreated={() => { setOpen(false); load(); toast.success("Case opened"); }} />}
        </div>
    );
}

function NewCaseModal({ onClose, clients, onCreated }) {
    const [form, setForm] = useState({ name: "", client_id: clients[0]?.client_id || "", case_type: "Corporate", description: "", status: "Intake" });
    const [newClient, setNewClient] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            let client_id = form.client_id;
            if (newClient.trim()) {
                const { data } = await api.post("/clients", { name: newClient.trim() });
                client_id = data.client_id;
            }
            if (!client_id) {
                toast.error("Pick or create a client");
                setBusy(false);
                return;
            }
            await api.post("/cases", { ...form, client_id });
            onCreated();
        } catch (err) {
            toast.error("Failed to create case");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <form onSubmit={submit} className="bg-white rounded-sm max-w-lg w-full p-7 shadow-xl border border-slate-200 animate-fade-up" data-testid="new-case-modal">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="font-display text-2xl font-bold">Open a new case</h3>
                    <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="label-sharp">Case name</label>
                        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-sharp" data-testid="case-name-input" placeholder="e.g. Chen v. Pacific Logistics" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-sharp">Client (existing)</label>
                            <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="input-sharp" data-testid="case-client-select">
                                <option value="">— none —</option>
                                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-sharp">…or new client</label>
                            <input value={newClient} onChange={(e) => setNewClient(e.target.value)} className="input-sharp" placeholder="New client name" data-testid="case-new-client-input" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-sharp">Type</label>
                            <select value={form.case_type} onChange={(e) => setForm({ ...form, case_type: e.target.value })} className="input-sharp" data-testid="case-type-select">
                                {CASE_TYPES.map((t) => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-sharp">Status</label>
                            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="input-sharp">
                                {["Intake", "Discovery", "Negotiation", "Trial", "Closed"].map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="label-sharp">Description</label>
                        <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-sharp" data-testid="case-description-input" />
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
                    <button disabled={busy} type="submit" className="btn-primary disabled:opacity-60" data-testid="case-submit-button">
                        {busy ? "Creating…" : "Open case"}
                    </button>
                </div>
            </form>
        </div>
    );
}
